import { FunctionComponent, ReactNode } from "react";
import { Dialog, DialogActions, DialogContent } from "@mui/material";

import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";

import { faClose } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Button } from "../Button/Button";
import { ButtonColor } from "../Button/types";
import { Checkbox } from "../Checkbox/Checkbox";
import { Icon } from "../Icon/Icon";

interface ModalProps {
	children: ReactNode;
	title: string;
	subtitle?: string;
	isOpened: boolean;
	handleClose?: () => void;
	handleClear?: () => void;
	handleSave?: () => void;
	handleThirdSave?: () => void;
	handleSecondSave?: () => void;
	secondSubmitButtonText?: string;
	cancelButtonText?: string;
	thirdSubmitButtonText?: string;
	isLoading?: boolean;
	submitButtonText?: string;
	submitButtonColor?: string;
	clearButtonText?: string;
	clearButtonColor?: string;
	size?: "xs" | "sm" | "md" | "lg" | "xl";
	overflow?: boolean;
	checkbox?: boolean;
	handleCheckboxClick?: () => void;
	checkboxLabel?: string;
	disableSave?: boolean;
	disableClose?: boolean;
	aboveHeader?: ReactNode;
	submitButtonIcon?: JSX.Element;
	showActions?: boolean;
	dialogContentClassName?: string;
	saveButtonContainerClassName?: string;
	saveButtonClassName?: string;
	modalContainerClassName?: string;
	isCloseShowing?: boolean;
}

export const Modal: FunctionComponent<ModalProps> = ({
	title,
	subtitle,
	children,
	isOpened,
	handleClose,
	handleSave,
	handleSecondSave,
	handleThirdSave,
	checkbox,
	handleCheckboxClick,
	secondSubmitButtonText,
	thirdSubmitButtonText,
	submitButtonText,
	submitButtonColor,
	isLoading = false,
	size = "xs",
	handleClear,
	isCloseShowing,
	clearButtonText,
	clearButtonColor,
	overflow,
	disableClose,
	checkboxLabel,
	disableSave,
	aboveHeader,
	submitButtonIcon,
	showActions = true,
	dialogContentClassName = "",
	cancelButtonText,
	saveButtonContainerClassName,
	saveButtonClassName,
	modalContainerClassName,
}) => {
	const { t } = useTranslation();

	const cssForOverflow = overflow
		? {
				"& .MuiDialogContent-root": {
					overflow: "visible",
				},
			}
		: {};

	return (
		<Dialog
			fullWidth
			maxWidth={size}
			open={isOpened}
			// The page behind the dialog scrolls, not a box inside it. The paper lets its content
			// overflow (for dropdowns), so a dialog taller than the window used to run off the
			// screen with its buttons out of reach (BCM360-380).
			scroll="body"
			sx={{
				overflow: "visible!important",
				"& .MuiDialog-paper": {
					borderRadius: "2xl",
					overflow: "visible",
				},
				"& .MuiDialogContent-root": {
					overflowY: "auto",
				},
				...cssForOverflow,
			}}
			onAbort={handleClose}
			// onClose={handleClose ? handleClose : handleSave}
		>
			<Button
				// Inside the panel's own top-right corner. Negative offsets used to put it
				// above and right of the dialog, floating over whatever was behind — reading as
				// a stray control rather than this dialog's way out. One shared component, so
				// it was every dialog in the product.
				className="!w-9 !h-9 !p-0 border-0 bg-transparent absolute !rounded-lg top-2 right-2 z-10"
				disabled={isLoading || disableClose}
				image={<Icon className="text-primary-gray w-5 h-5" icon={faClose} />}
				title=""
				onClick={() => (handleClose ? handleClose?.() : handleSave?.())}
			/>
			<div className={`!p-1 !bg-surface !rounded-[20px] ${modalContainerClassName}`}>
				<div className="bg-white rounded-16 p-4 shadow-[0px_1px_2px_0px_rgba(28,39,76,0.12)]">
					{aboveHeader}
					{title && (
						<div
							className={
								aboveHeader
									? `text-ssmd mx-6 mt-[6.5rem] mb-1 z-10 font-bold relative`
									: `text-md mx-6 mt-6 mb-1`
							}
						>
							{title}
						</div>
					)}
					{subtitle && <div className="text-sm mx-6 mt-1 mb-4">{subtitle ?? ""}</div>}

					<DialogContent
						className={`custom-scrollbar ${dialogContentClassName}`}
						sx={{ pt: 2, pb: 4, height: "fit-content", overflow: "auto", zIndex: 1 }}
					>
						{children}
					</DialogContent>
				</div>
				{showActions && (
					<DialogActions sx={{ px: 3, py: 2, overflow: "visible" }}>
						{handleCheckboxClick && typeof checkbox === "boolean" && (
							<div className="w-[150px] flex items-center">
								<Checkbox
									className="whitespace-nowrap"
									isChecked={checkbox}
									onChange={handleCheckboxClick}
								>
									{checkboxLabel}
								</Checkbox>
							</div>
						)}
						<div className={classNames("w-full flex justify-end gap-2")}>
							{handleClear && (
								<div className="w-[150px]">
									<Button
										color={
											clearButtonColor
												? (clearButtonColor as ButtonColor)
												: ButtonColor.ACTION_SECONDARY
										}
										title={clearButtonText ?? t("basics.clear")}
										onClick={handleClear}
									/>
								</div>
							)}
							<Stack alignItems="center" direction="row" flexWrap="wrap" spacing={2}>
								{handleClose && !isCloseShowing && (
									<div className="w-[180px]">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											disabled={isLoading || disableClose}
											title={cancelButtonText ?? t("basics.cancel")}
											onClick={handleClose}
										></Button>
									</div>
								)}

								{handleSecondSave && (
									<div className="w-[180px]">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											isLoading={isLoading}
											title={secondSubmitButtonText ?? t("basics.saveChanges")}
											onClick={handleSecondSave}
										></Button>
									</div>
								)}

								{handleThirdSave && (
									<div className="w-[220px]">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											isLoading={isLoading}
											title={thirdSubmitButtonText ?? t("basics.saveChanges")}
											onClick={handleThirdSave}
										></Button>
									</div>
								)}

								{handleSave && (
									<div className={`w-fit min-w-[180px] ${saveButtonContainerClassName ?? ""}`}>
										<Button
											className={saveButtonClassName}
											color={
												submitButtonColor ? (submitButtonColor as ButtonColor) : ButtonColor.ACTION
											}
											disabled={isLoading || disableSave}
											image={submitButtonIcon}
											isLoading={isLoading}
											title={submitButtonText ?? t("basics.saveChanges")}
											onClick={handleSave}
										></Button>
									</div>
								)}
							</Stack>
						</div>
					</DialogActions>
				)}
			</div>
		</Dialog>
	);
};
